import { Box, Flex, Subheading } from "../Styles";
import styled from "styled-components";
import * as SiIcons from "react-icons/si";
import type { SVGProps } from "react";
import Tooltip from "../Tooltip";

const ProjectItem = styled(Flex)`
  margin-bottom: 4rem;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(74, 127, 229, 0.4);
    box-shadow: 0 12px 40px rgba(74, 127, 229, 0.15);
    transform: translateY(-4px);
  }

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 3rem;
    align-items: center;
  }
`;

const ProjectContent = styled(Box)`
  flex: 1;
  max-width: 100%;
  
  @media (min-width: 768px) {
    max-width: 500px;
  }
`;

const ProjectImage = styled.img<{ imageOrientation: "portrait" | "landscape" }>`
  object-fit: cover;
  border-radius: 12px;
  max-width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;
  
  ${ProjectItem}:hover & {
    transform: scale(1.02);
  }

  ${({ imageOrientation }) =>
    imageOrientation === "portrait" &&
    `
    width: 200px;
    height: 300px;
  `}
  ${({ imageOrientation }) =>
    imageOrientation === "landscape" &&
    `
    width: 100%;
    height: 250px;
    @media (min-width: 768px) {
      width: 600px;
      max-width: 100%;
      height: 300px;
    }
  `}
`;

const Description = styled.span`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  line-height: 1.7;
  margin: 1rem 0;
  display: block;
  font-weight: 300;
`;

const TechList = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const TechIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  padding: 0.6rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1) translateY(-2px);
    background: rgba(74, 127, 229, 0.2);
    border-color: rgba(74, 127, 229, 0.4);
    box-shadow: 0 4px 12px rgba(74, 127, 229, 0.3);
  }
`;

const ProjectsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const ProjectTitle = styled(Subheading)`
  font-size: 1.75rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #4a7fe5, #7d9ced, #a7baf3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  font-weight: 700;
`;

export type ProjectImageOrientation = "portrait" | "landscape";
export type Tech = { name: string; icon: keyof typeof SiIcons; color: string };
export interface Project {
  title: string;
  description: string;
  image: string | any; // Allow both string URLs and imported images
  imageOrientation: ProjectImageOrientation;
  techs: Tech[];
}

interface ProjectsProps {
  projects: Project[];
}

const Projects = ({ projects }: ProjectsProps) => {
  return (
    <ProjectsContainer id="projects">
      {projects.map((project, index) => (
        <ProjectItem key={index}>
          <ProjectContent>
            <ProjectTitle>{project.title}</ProjectTitle>
            <TechList>  
              {project.techs.map((tech, techIndex) => {
                const Icon = SiIcons[tech.icon] as React.ComponentType<
                  SVGProps<SVGSVGElement>
                >;

                if (!Icon) {
                  console.error(`Icon for ${tech.name} not found`);
                  return null;
                }

                return (
                  <Tooltip key={`${project.title}-${tech.name}-${techIndex}`} content={tech.name}>
                    <TechIcon>
                      <Icon color={tech.color} width={24} height={24} />
                    </TechIcon>
                  </Tooltip>
                );
              })}
            </TechList>
            <Description>{project.description}</Description>
          </ProjectContent>
          <ProjectImage
            src={project.image}
            alt={project.title}
            imageOrientation={project.imageOrientation}
          />
        </ProjectItem>
      ))}
    </ProjectsContainer>
  );
};

export default Projects;
